import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axios/axiosInstance";
import { clearUser, getUser, setAuthUser } from "../../redux/slices/AuthUser";
import { useShowAlert } from "../../hooks/useShowAlert";
import { useNavigate } from "react-router-dom";
import ProfileCompletionDialog from "./ProfileCompletionDialog";

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Email domains the social-login flow uses as placeholders.
const PLACEHOLDER_EMAIL_DOMAINS = [
  "@privaterelay.appleid.com",
  "@appleid.directrent.com",
  "@fb.directrent.com",
];

const isPlaceholderEmail = (email) => {
  if (!email) return true;
  const lower = email.toLowerCase();
  return PLACEHOLDER_EMAIL_DOMAINS.some((suffix) => lower.endsWith(suffix));
};

// Auto-generated usernames from usernameGenerator.js are a CamelCase
// Color+Animal+4digits string (e.g. "RedTiger4821", "GreenWolf5023").
// Now that valid chosen usernames can also be single words (e.g. "Mohamed",
// "John99"), we detect auto-generated ones by their specific pattern:
// uppercase letter, lowercase letters, ANOTHER uppercase letter (CamelCase),
// lowercase letters, then exactly 4 digits.
const AUTO_GENERATED_RE = /^[A-Z][a-z]+[A-Z][a-z]+\d{4}$/;
const isAutoGeneratedUsername = (username) => {
  if (!username) return true;
  return AUTO_GENERATED_RE.test(username.trim());
};

// A phone is "verified" if isPhoneVerified is true, OR for backward compat
// (existing users before the flag existed), if the user was activated AND has a
// phone — those users already went through phone-OTP during signup.
const isPhoneVerified = (user) =>
  user?.isPhoneVerified ||
  (user?.isActivated && !!user?.phoneNumber);

/**
 * ProfileCompletionGate
 *
 * Sits at the app root (router.jsx) and ensures **every** authenticated user
 * has a complete profile before they can use the app:
 *   1. First name & last name
 *   2. A chosen username (not auto-generated)
 *   3. A real email (not a placeholder relay)
 *   4. A verified phone number (unique, one per account)
 *
 * Displays a mandatory multi-step dialog — no skip, no close, no escape.
 */
const ProfileCompletionGate = () => {
  const { user, id, exp } = useSelector(getUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showAlert } = useShowAlert();
  const { t } = useTranslation("profileCompletion");

  const [open, setOpen] = useState(false);
  const [personalLoading, setPersonalLoading] = useState(false);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [otpSendLoading, setOtpSendLoading] = useState(false);
  const [otpVerifyLoading, setOtpVerifyLoading] = useState(false);
  const [error, setError] = useState("");

  // ── What's missing? ──────────────────────────────────────────────────────

  const needsPersonalInfo = useMemo(() => {
    if (!user || !id) return false;
    const missingName = !user.firstName?.trim() || !user.lastName?.trim();
    const autoUsername = isAutoGeneratedUsername(user.username);
    const placeholderEmail = isPlaceholderEmail(user.email);
    return missingName || autoUsername || placeholderEmail;
  }, [user, id]);

  const needsPhone = useMemo(() => {
    if (!user || !id) return false;
    return !user.phoneNumber || !isPhoneVerified(user);
  }, [user, id]);

  const needsProfile = needsPersonalInfo || needsPhone;

  // Open the dialog whenever the user has missing fields
  useEffect(() => {
    if (!user || !id) {
      setOpen(false);
      return;
    }
    setOpen(needsProfile);
  }, [needsProfile, user, id]);

  // ── Determine starting step ──────────────────────────────────────────────
  // 0 = personal info, 1 = phone, 2 = otp

  const startStep = needsPersonalInfo ? 0 : 1;

  // ── Initial values for step 1 ────────────────────────────────────────────

  const personalInitialValues = useMemo(() => {
    if (!needsPersonalInfo) return null; // skip step 1
    return {
      firstName: user?.firstName?.trim() || "",
      lastName: user?.lastName?.trim() || "",
      username: isAutoGeneratedUsername(user?.username)
        ? ""
        : user?.username || "",
      email: isPlaceholderEmail(user?.email) ? "" : user?.email || "",
    };
  }, [user, needsPersonalInfo]);

  const showUsernameField = isAutoGeneratedUsername(user?.username);
  const showEmailField = isPlaceholderEmail(user?.email);

  // ── Refresh Redux user from server ───────────────────────────────────────

  const refreshUser = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/user/current", {
        skipAuthLogout: true,
      });
      const fresh = res.data?.user || null;
      if (fresh) {
        dispatch(setAuthUser({ user: fresh, exp, id }));
      }
      return fresh;
    } catch {
      return null;
    }
  }, [dispatch, exp, id]);

  // ── Step 1: Save personal info ───────────────────────────────────────────

  const handlePersonalInfoSubmit = useCallback(
    async (values) => {
      if (!id) return false;
      setPersonalLoading(true);
      setError("");

      try {
        const payload = {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          displayName:
            `${values.firstName.trim()} ${values.lastName.trim()}`.trim(),
        };

        if (showUsernameField && values.username) {
          payload.username = values.username.trim();
        }
        if (showEmailField && values.email) {
          payload.email = values.email.trim();
        }

        await axiosInstance.put(`/user/${id}`, payload);
        await refreshUser();

        // If email was changed, send verification (non-blocking)
        if (showEmailField && values.email) {
          try {
            await axiosInstance.post("/user/generate-email-token", {
              email: values.email.trim(),
            });
          } catch {
            // Non-critical — they can verify later
          }
        }

        setPersonalLoading(false);
        return true;
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            t("errors.saveFailed")
        );
        setPersonalLoading(false);
        return false;
      }
    },
    [id, showUsernameField, showEmailField, refreshUser, t]
  );

  // ── Step 2: Save phone number ────────────────────────────────────────────

  const handleSavePhone = useCallback(
    async (phoneNumber) => {
      if (!id) return false;
      setPhoneLoading(true);
      setError("");

      try {
        await axiosInstance.put(`/user/${id}`, { phoneNumber });
        // Don't refresh yet — we'll refresh after OTP verification
        setPhoneLoading(false);
        return true;
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            t("errors.saveFailed")
        );
        setPhoneLoading(false);
        return false;
      }
    },
    [id, t]
  );

  // ── Step 2 → 3: Send OTP ────────────────────────────────────────────────

  const handleSendOtp = useCallback(
    async (phoneNumber, channel) => {
      setOtpSendLoading(true);
      setError("");

      try {
        await axiosInstance.post("/user/generate-phone-token", {
          phoneNumber,
          channel,
        });
        setOtpSendLoading(false);
        return true;
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            t("errors.otpSendFailed")
        );
        setOtpSendLoading(false);
        return false;
      }
    },
    [t]
  );

  // ── Step 3: Verify OTP ───────────────────────────────────────────────────

  const handleVerifyOtp = useCallback(
    async (phoneNumber, code) => {
      setOtpVerifyLoading(true);
      setError("");

      try {
        await axiosInstance.post("/user/verify-phone", {
          phoneNumber,
          verifyToken: code,
        });

        // Refresh user — now isPhoneVerified & isActivated should be true
        await refreshUser();

        showAlert(
          "success",
          t("saved", { defaultValue: "Profile updated." })
        );
        setOtpVerifyLoading(false);
        setOpen(false);
      } catch (err) {
        const msg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          t("errors.otpVerifyFailed");
        setError(msg);
        setOtpVerifyLoading(false);
      }
    },
    [refreshUser, showAlert, t]
  );

  // ── Cancel: log out and go home ───────────────────────────────────────

  const handleCancel = useCallback(() => {
    dispatch(clearUser());
    localStorage.removeItem("token");
    axiosInstance.post("/user/logout").catch(() => {});
    setOpen(false);
    navigate("/", { replace: true });
  }, [dispatch, navigate]);

  // ── Render ───────────────────────────────────────────────────────────────

  if (!user || !id || !needsProfile) return null;

  return (
    <ProfileCompletionDialog
      open={open}
      startStep={startStep}
      totalSteps={personalInitialValues ? 3 : 2}
      // Step 1
      personalInitialValues={personalInitialValues}
      onPersonalInfoSubmit={handlePersonalInfoSubmit}
      personalLoading={personalLoading}
      showUsernameField={showUsernameField}
      showEmailField={showEmailField}
      // Step 2 & 3
      userId={id}
      initialPhone={user?.phoneNumber || ""}
      onSavePhone={handleSavePhone}
      phoneLoading={phoneLoading}
      onSendOtp={handleSendOtp}
      otpSendLoading={otpSendLoading}
      onVerifyOtp={handleVerifyOtp}
      otpVerifyLoading={otpVerifyLoading}
      // Shared
      error={error}
      onClearError={() => setError("")}
      onCancel={handleCancel}
    />
  );
};

export default ProfileCompletionGate;
